import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  Typography,
  Tooltip,
} from '@material-ui/core'
import { useTranslate } from 'react-admin'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import MyEditIcon from '../../../assets/icons/MyEditIcon'
import MyDeleteIcon from '../../../assets/icons/MyDeleteIcon'
import courtsListStyles from './courtsList.styles'
import QueryKeys from '../../../utils/constants/queryKeys'
import { deleteCourt } from '../../../Apis/courts/deleteCourt'
import courtType from '../../../types/courtType'
import IsUserRolesAuthorized from '../../../components/IsUserRolesAuthorized/IsUserRolesAuthorized'
import USER_ORG_ROLES from '../../../utils/constants/userOrgRoles'
import { useCourtStatusHubContext } from '../../../context/useCourtStatusHubContext'

const CourtsList = ({ courts, isCourtsLoading, editedCourtId, setEditedCourtId, page, perPage, totalCount, onPageChange }) => {
  const [showButtons, setShowButtons] = useState(-1)
  const queryClient = useQueryClient()
  const translate = useTranslate()
  const useStyles = makeStyles(courtsListStyles)
  const classes = useStyles()
  const { courtStatuses } = useCourtStatusHubContext()

  const deleteACourt = useMutation((data) => deleteCourt(data), {
    onSuccess: () => queryClient.invalidateQueries(QueryKeys.GET_COURTS),
  })

  const handleDelete = (courtId) => deleteACourt.mutate({ courtId })
  const handleEdit = (courtId) => setEditedCourtId(courtId)

  // While Up every check passes; while Down the failing ones are listed.
  const getStatusTooltip = (courtStatus) => {
    if (courtStatus.isUp) {
      return ''
    }

    const failures = Object.entries(courtStatus.checkResults ?? {})
      .filter(([, success]) => success === false)
      .map(([checkName]) => `Cannot connect to ${checkName}`)

    if (failures.length === 0) return 'Court connection failed'
    if (failures.length === 1) return failures[0]
    return (
      <div>
        {failures.map((failure) => (
          <div key={failure}>{failure}</div>
        ))}
      </div>
    )
  }

  const getCourtStatusIndicator = (court) => {
    if (!court.livestreamEnabled) {
      return null
    }

    const courtStatus = courtStatuses[court.id]

    if (!courtStatus) {
      return (
        <div
          style={{
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: '#ccc',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 'bold',
            display: 'inline-block',
            minWidth: '32px',
            textAlign: 'center',
          }}
        >
          --
        </div>
      )
    }

    return (
      <Tooltip title={getStatusTooltip(courtStatus)} placement='top'>
        <div
          style={{
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: courtStatus.isUp ? '#4caf50' : '#f44336',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 'bold',
            display: 'inline-block',
            cursor: 'help',
            minWidth: '32px',
            textAlign: 'center',
          }}
        >
          {courtStatus.isUp ? 'ONLINE' : 'OFFLINE'}
        </div>
      </Tooltip>
    )
  }

  const filterCourts = () => {
    if (editedCourtId) {
      return courts?.filter((court) => court.id !== editedCourtId)
    }
    return courts
  }

  return !filterCourts()?.length && !isCourtsLoading && !editedCourtId ? (
    <Typography className={classes.emptyCourt} variant='body2'>
      {translate('ra.text.emptyCourtsList')}
    </Typography>
  ) : (
    <>
      <Table>
        <TableBody>
          {filterCourts()?.map((court) => (
            <TableRow
              key={court.id}
              hover
              onMouseEnter={() => setShowButtons(court.id)}
              onMouseLeave={() => setShowButtons(-1)}
            >
              <TableCell>{court.name}</TableCell>
              <TableCell>{court.description}</TableCell>
              <TableCell>{getCourtStatusIndicator(court)}</TableCell>
              <TableCell>
                <div
                  className={classnames(classes.buttonsWrapper, {
                    [classes.buttonsWrapperHidden]: showButtons !== court.id,
                  })}
                >
                  <IsUserRolesAuthorized
                    acceptedRoles={[
                      USER_ORG_ROLES.OWNER,
                      USER_ORG_ROLES.ADMIN,
                    ]}
                  >
                    {court.isUserEditable && (
                      <MyEditIcon
                        className={classes.orangeButton}
                        onClick={() => handleEdit(court.id)}
                      />
                    )}
                    {court.isUserEditable && (
                      <MyDeleteIcon
                        className={classes.orangeButton}
                        onClick={() => handleDelete(court.id)}
                      />
                    )}
                  </IsUserRolesAuthorized>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {totalCount > perPage && (
        <TablePagination
          component='div'
          count={totalCount}
          page={page}
          rowsPerPage={perPage}
          rowsPerPageOptions={[perPage]}
          onChangePage={(_, newPage) => onPageChange(newPage)}
        />
      )}
    </>
  )
}

CourtsList.propTypes = {
  courts: PropTypes.arrayOf(courtType),
  isCourtsLoading: PropTypes.bool,
  editedCourtId: PropTypes.string,
  setEditedCourtId: PropTypes.func,
  page: PropTypes.number,
  perPage: PropTypes.number,
  totalCount: PropTypes.number,
  onPageChange: PropTypes.func,
}

export default CourtsList
